import React, { useState, useEffect, useRef, ReactNode } from 'react';
import styled from 'styled-components';
import HBClickable from '../HBClickable/HBClickable';
import HBText from '../HBText/HBText';
import { hideInPrint, printCss } from '../../styles/mixins';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export interface HBTextTruncatorProps {
    /** Maximum number of visible lines before truncation; set to 0 to disable clamping. */
    lines: number;
    /** Label for the toggle button when content is truncated (e.g. "Read more"). */
    readMoreText: string;
    /** Label for the toggle button when content is fully expanded (e.g. "Read less"). */
    readLessText: string;
    children: ReactNode;
}

export default function HBTextTruncator({
    testIdName,
    testId,
    lines,
    readMoreText,
    readLessText,
    children
}: HBTextTruncatorProps & MustIncludeOneOfBasicTestIdProps) {
    const [isClamped, setIsClamped] = useState(lines > 0);
    const [isOverflowed, setIsOverflowed] = useState(false);
    const clamperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = clamperRef.current;
        if (!container || !isClamped) {
            return;
        }
        const checkOverflow = () => {
            const isOverflow = container.scrollHeight > container.clientHeight;
            setIsOverflowed(isOverflow);
        };
        checkOverflow();
        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [children, lines, isClamped]);

    const toggleClamp = () => setIsClamped(prev => !prev);

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'text-truncator',
        componentName: 'HBTextTruncator'
    });

    return (
        <Container
            data-testid={finalTestId ? `${finalTestId}-wrapper` : undefined}
        >
            <Clamper clamp={isClamped} ref={clamperRef} lines={lines}>
                {children}
            </Clamper>
            {isOverflowed && (
                <ToggleButton
                    onClick={toggleClamp}
                    testId={finalTestId ? `${finalTestId}-toggle` : undefined}
                >
                    <HBText type="body14">
                        {isClamped ? readMoreText : readLessText}
                    </HBText>
                </ToggleButton>
            )}
        </Container>
    );
}

// -------------------------------- Styles -------------------------------- //
const Container = styled.div``;

const Clamper = styled.div<{ clamp: boolean; lines: number }>`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: ${({ clamp, lines }) => (clamp ? lines : '')};
    white-space: pre-wrap;

    ${printCss(`
        -webkit-line-clamp: unset;
    `)}
`;

const ToggleButton = styled(HBClickable)`
    text-decoration: underline;
    width: fit-content;
    ${hideInPrint}
`;
